import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Home, Building, Key } from "lucide-react";
import { useCompanyInfo } from "@/hooks/use-company-info";

export function HeroSection() {
  const { tagline, company_name } = useCompanyInfo();
  return (
    <section className="relative min-h-[85vh] flex items-center">
      {/* Background Image - Using img tag for LCP optimization */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1564013799919-ab600027ffc6?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=40"
          srcSet="https://images.unsplash.com/photo-1564013799919-ab600027ffc6?ixlib=rb-4.0.3&auto=format&fit=crop&w=480&q=40 480w, https://images.unsplash.com/photo-1564013799919-ab600027ffc6?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=40 800w, https://images.unsplash.com/photo-1564013799919-ab600027ffc6?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=50 1200w"
          sizes="100vw"
          alt="Beautiful property exterior"
          className="w-full h-full object-cover"
          fetchPriority="high"
          decoding="sync"
          width={1200}
          height={800}
        />
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-[hsl(30_20%_20%/0.5)] to-[hsl(30_20%_20%/0.7)]" />
      </div>
      
      <div className="container relative z-10 py-20">
        <div className="max-w-3xl">
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight animate-fade-in">
            {tagline}
          </h1>
          <p className="text-lg md:text-xl text-white/90 mb-8 leading-relaxed animate-fade-in" style={{ animationDelay: "0.1s" }}>
            Discover exceptional vacation rentals, commercial spaces, and long-term homes with {company_name}. We make finding your perfect property effortless.
          </p>
          <div className="flex flex-wrap gap-4 animate-fade-in" style={{ animationDelay: "0.2s" }}>
            <Button size="lg" asChild className="bg-primary hover:bg-primary/90">
              <Link to="/properties">
                View Properties
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild className="bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white">
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>

        {/* Property Type Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16 animate-fade-in" style={{ animationDelay: "0.3s" }}>
          <Link 
            to="/properties?type=short-term"
            className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 hover:bg-white/20 transition-all group"
          >
            <Home className="h-8 w-8 text-primary mb-4" />
            <h2 className="font-display text-xl font-semibold text-white mb-2">Short-Term Rentals</h2>
            <p className="text-white/70 text-sm">Perfect vacation getaways with all the comforts of home.</p>
          </Link>
          
          <Link 
            to="/properties?type=commercial"
            className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 hover:bg-white/20 transition-all group"
          >
            <Building className="h-8 w-8 text-primary mb-4" />
            <h2 className="font-display text-xl font-semibold text-white mb-2">Commercial Properties</h2>
            <p className="text-white/70 text-sm">Prime commercial spaces for your business needs.</p>
          </Link>
          
          <Link 
            to="/properties?type=long-term"
            className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 hover:bg-white/20 transition-all group"
          >
            <Key className="h-8 w-8 text-primary mb-4" />
            <h2 className="font-display text-xl font-semibold text-white mb-2">Long-Term Rentals</h2>
            <p className="text-white/70 text-sm">Find your new home with flexible leasing options.</p>
          </Link>
        </div>
      </div>
    </section>
  );
}
